import React, { useState } from 'react';
import {
  Autocomplete,
  TextField,
  Button,
  Box,
  Typography,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import { DateTime } from 'luxon';
import fetchQueryResults from '../../services/query';
import ErrorAlert from '../../components/Alert/ErrorAlert';
import { getStoredRoles, isFodRole } from '../../helpers/auth';
import { downloadFodPlotFromCsvUrl } from '../../services/generateFodPlot';

const sensorTypes = [
  { label: 'Met Rate', value: 'MET_RATE' },
  { label: 'Heart Rate', value: 'HEART_RATE' },
  { label: 'Temp Humid', value: 'TEMP_HUMID' },
  { label: 'ECS Suit', value: 'ECS_SUIT' },
];

const allFacilities = [
  'NBL',
  'ARGOS',
  'CHAPEA',
  'FIELD TEST',
];

function QueryPage() {
  const userRoles = getStoredRoles();
  const isFod = isFodRole(userRoles);
  const facilities = isFod ? ['NBL'] : allFacilities;
  const [selectedDate, setSelectedDate] = useState(null);
  const [startTime, setStartTime] = useState(null); // auto-set on date change
  const [endTime, setEndTime] = useState(null); // auto-set on date change
  const [selectedSensors, setSelectedSensors] = useState([...sensorTypes]);
  const [selectedFacility, setSelectedFacility] = useState(isFod ? 'NBL' : null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isFodPlotLoading, setIsFodPlotLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isDateValid, setIsDateValid] = useState(true);

  const createCentralTime = (date, time) => {
    if (!date) return null;
    const centralTime = DateTime.fromObject(
      {
        year: date.year,
        month: date.month,
        day: date.day,
        hour: time ? time.hour : 0,
        minute: time ? time.minute : 0,
        second: 0,
        millisecond: 0,
      },
      { zone: 'America/Chicago' },
    );
    return centralTime;
  };

  const handleDateChange = (newDate) => {
    if (newDate && DateTime.isDateTime(newDate) && newDate.isValid) {
      setIsDateValid(true);
      setSelectedDate(newDate);
      setStartTime(createCentralTime(newDate, { hour: 0, minute: 0 }));
      setEndTime(createCentralTime(newDate, { hour: 23, minute: 59 }));
    } else {
      setIsDateValid(false);
      setSelectedDate(null);
      setStartTime(null);
      setEndTime(null);
    }
  };

  const handleErrorClose = () => {
    setError(null);
  };

  const handleFodPlotDownload = async (csvUrl) => {
    setIsFodPlotLoading(true);
    setError(null);
    try {
      const isoDate = selectedDate ? selectedDate.toISODate() : '';
      await downloadFodPlotFromCsvUrl({
        csvUrl,
        timelineDataUrl: null,
        title: `NBL | ${isoDate}`,
        filename: `fod-nbl-plot-${isoDate || 'download'}.pdf`,
      });
    } catch (e) {
      setError(`Failed to generate FOD plot: ${e.message}`);
    } finally {
      setIsFodPlotLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!selectedDate || !startTime || !endTime || selectedSensors.length === 0 || !selectedFacility) { // startTime/endTime auto-set from date
      return;
    }

    setIsLoading(true);
    setError(null);
    setDownloadUrl(null);

    try {
      const data = await fetchQueryResults({
        startTime,
        endTime,
        selectedSensors: selectedSensors.map((s) => s.value),
        selectedFacility,
      });

      if (data.error === 'No data found for the specified parameters') {
        setError('No data was found for the selected parameters. Please try different search criteria.');
      } else if (data.csv_url) {
        setDownloadUrl(data.csv_url);
      }
    } catch (apiError) {
      if (apiError.response) {
        setError(`Server error: ${apiError.response.status} - ${apiError.response.data?.message || 'Unknown error'}`);
      } else if (apiError.request) {
        setError('Unable to reach the server. Please check your internet connection and try again.');
      } else {
        setError(`Error: ${apiError.message}`);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      {error && <ErrorAlert message={error} onClose={handleErrorClose} />}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 3,
          padding: 4,
          maxWidth: 465,
          margin: '0 auto',
        }}
      >
        <Typography variant="h5" component="h1" gutterBottom>
          Query
        </Typography>

        <Autocomplete
          options={facilities}
          value={selectedFacility}
          onChange={(event, newValue) => setSelectedFacility(newValue)}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Facility"
              placeholder="Select facility"
            />
          )}
          sx={{ width: '100%', maxWidth: 450, minWidth: 450 }}
        />

        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <Box sx={{ width: '100%', maxWidth: 450, minWidth: 450 }}>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={handleDateChange}
              disableMaskedInput
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  error={!isDateValid}
                  helperText={!isDateValid ? 'Please enter a valid date.' : ''}
                />
              )}
            />
          </Box>
        </LocalizationProvider>

        <Autocomplete
          multiple
          options={sensorTypes}
          value={selectedSensors}
          onChange={(event, newValue) => setSelectedSensors(newValue)}
          getOptionLabel={(option) => option.label}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Sensor Types"
              placeholder="Select sensor types"
            />
          )}
          sx={{ width: '100%', maxWidth: 450, minWidth: 450 }}
        />

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={
            isLoading
            || !selectedDate
            || selectedSensors.length === 0
            || !selectedFacility
            || !isDateValid
          }
          sx={{ width: '100%', maxWidth: 450, minWidth: 450 }}
        >
          {isLoading ? 'Requesting Data...' : 'Request Data'}
        </Button>

        {downloadUrl && isFod && (
          <Button
            variant="outlined"
            onClick={() => handleFodPlotDownload(downloadUrl)}
            disabled={isFodPlotLoading}
            sx={{ width: '100%', maxWidth: 450, minWidth: 450 }}
          >
            {isFodPlotLoading ? 'Generating Plot...' : 'Download FOD Plot'}
          </Button>
        )}
        {downloadUrl && !isFod && (
          <Button
            variant="outlined"
            href={downloadUrl}
            download
            sx={{ width: '100%', maxWidth: 450, minWidth: 450 }}
          >
            Download CSV
          </Button>
        )}
      </Box>
    </>
  );
}

export default QueryPage;
